// BS 5534 / BS 5250 specification panel. Renders per-section spec
// resolved by the backend specifications.py engine. Surfaces batten
// gauge, fixing schedule, headlap, ventilation, tile take-off and
// estimated weight — i.e. the data that distinguishes a professional
// specification from a basic estimate.
import React from "react";
import { ShieldCheck, CaretRight, CaretDown } from "@phosphor-icons/react";

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-2 text-xs py-1">
      <span className="text-slate-500">{label}</span>
      <span className="font-mono-data font-bold text-right">{value}</span>
    </div>
  );
}

function SectionSpec({ name, spec, index, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  if (!spec) return null;
  const cv = spec.covering_resolved;
  const vent = spec.ventilation || {};
  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid={`spec-section-${index}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-slate-50"
        data-testid={`spec-section-toggle-${index}`}
      >
        {open ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold text-sm">{name}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {cv?.name || cv?.category || "No covering set"} {cv?.ref ? `· ${cv.ref}` : ""}
          </div>
        </div>
        <div className="font-mono-data text-xs font-bold text-[var(--rqp-blue)]">
          {spec.batten_gauge_mm ? `${spec.batten_gauge_mm} mm gauge` : "—"}
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-200 p-3 space-y-2">
          {!cv ? (
            <div className="text-xs text-slate-500 italic">
              Set a covering category or first covering name to resolve the BS 5534 specification for this section.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-4">
                <Row label="Min pitch" value={`${spec.min_pitch_deg}°`} />
                <Row label="Max pitch" value={`${spec.max_pitch_deg}°`} />
                <Row label="Headlap" value={spec.headlap_mm ? `${spec.headlap_mm} mm` : "—"} />
                <Row label="Batten gauge" value={spec.batten_gauge_mm ? `${spec.batten_gauge_mm} mm` : "—"} />
                <Row label="Tiles / m²" value={spec.tiles_per_m2 ?? "—"} />
                <Row label="Tile dimensions" value={spec.tile_dimensions_mm ?? "—"} />
                <Row label="Weight" value={spec.weight_kg_per_m2 ? `${spec.weight_kg_per_m2} kg/m²` : "—"} />
                <Row label="Tile take-off (incl. waste)" value={spec.tile_quantity || "—"} />
              </div>

              <div className="pt-2 border-t border-dashed border-slate-200">
                <div className="overline text-[var(--rqp-blue)]">Fixing schedule — BS 5534</div>
                <p className="text-xs mt-1 leading-relaxed">{spec.fixing_schedule}</p>
                <div className="text-[10px] text-slate-500 mt-1">
                  Nails: <span className="font-semibold">{spec.nail_type || "—"}</span> · Clips: <span className="font-semibold">{spec.clip_type || "—"}</span>
                </div>
              </div>

              {/* BS EN 1991-1-4 fixing calculator */}
              {spec.fixing_calc?.ok && (
                <div className="pt-2 border-t border-dashed border-slate-200" data-testid="fixing-calc-block">
                  <div className="overline text-[var(--rqp-blue)]">Mechanical fixing calc — BS EN 1991-1-4</div>
                  <div className="text-[10px] text-slate-500 mb-2">
                    qp = {spec.fixing_calc.inputs.qp_N_m2} N/m² · z = {spec.fixing_calc.inputs.building_height_m} m · terrain {spec.fixing_calc.inputs.terrain} · Vb = {spec.fixing_calc.inputs.Vb_m_s} m/s · SF = {spec.fixing_calc.inputs.safety_factor}
                  </div>
                  <table className="w-full text-[11px] font-mono-data">
                    <thead>
                      <tr className="text-slate-500 border-b border-slate-200">
                        <th className="text-left py-1">Zone</th>
                        <th className="text-right py-1">Cpe</th>
                        <th className="text-right py-1">Uplift / tile</th>
                        <th className="text-right py-1">Self-wt</th>
                        <th className="text-right py-1">Required</th>
                        <th className="text-left py-1 pl-2">Fixing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(spec.fixing_calc.zones).map((z) => (
                        <tr key={z.zone} className="border-b border-slate-100" data-testid={`fixing-zone-${z.zone}`}>
                          <td className="py-1 font-semibold">{z.zone} <span className="text-slate-500 font-normal">({z.label})</span></td>
                          <td className="py-1 text-right">{z.cpe}</td>
                          <td className="py-1 text-right">{z.uplift_per_tile_N} N</td>
                          <td className="py-1 text-right">{z.tile_self_weight_N} N</td>
                          <td className="py-1 text-right font-bold">{z.required_fixing_strength_N} N</td>
                          <td className="py-1 pl-2 text-[10px] text-slate-700">{z.fixing_recommendation}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="pt-2 border-t border-dashed border-slate-200">
                <div className="overline text-[var(--rqp-blue)]">Ventilation — BS 5250</div>
                <Row label="Eaves required" value={`${vent.required_eaves_mm2_per_m ?? 0} mm² / m`} />
                <Row label="Ridge required" value={`${vent.required_ridge_mm2_per_m ?? 0} mm² / m`} />
                <Row label="Total eaves" value={`${vent.total_eaves_mm2 ?? 0} mm²`} />
                <Row label="Total ridge" value={`${vent.total_ridge_mm2 ?? 0} mm²`} />
                <div className="text-[10px] text-slate-500 mt-1">
                  {vent.breathable_underlay_assumed ? "Breathable underlay assumed — reduced eaves requirement applied." : "Standard cold-roof ventilation."}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SpecificationsPanel({ specs, sections }) {
  const per = specs?.per_section || [];
  const agg = specs?.aggregate || {};
  if (!per.length) return null;
  return (
    <div className="card-elev p-5" data-testid="specifications-panel">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} weight="fill" className="text-[var(--rqp-blue)]" />
        <h3 className="font-display font-bold text-xl">BS 5534 / BS 5250 Specification</h3>
      </div>
      <p className="text-xs text-slate-500 mt-1">
        Generated from manufacturer technical data and the section measurements. Renders on the PDF quote.
      </p>

      {(agg.tile_quantity_total > 0 || agg.estimated_roof_weight_kg > 0) && (
        <div className="grid grid-cols-2 gap-2 mt-4">
          <div className="bg-slate-50 p-3 rounded-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Tile take-off</div>
            <div className="font-mono-data font-bold text-2xl text-[var(--rqp-orange)]">{agg.tile_quantity_total || 0}</div>
            <div className="text-[10px] text-slate-500">across all sections (incl. waste)</div>
          </div>
          <div className="bg-slate-50 p-3 rounded-sm">
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Roof weight</div>
            <div className="font-mono-data font-bold text-2xl text-[var(--rqp-blue)]">{Math.round(agg.estimated_roof_weight_kg || 0)} kg</div>
            <div className="text-[10px] text-slate-500">structural load estimate</div>
          </div>
        </div>
      )}

      <div className="space-y-2 mt-4">
        {per.map((p, i) => {
          const sec = sections && sections[i];
          return (
            <SectionSpec
              key={(sec && sec.id) || `spec-${i}`}
              index={i}
              name={(sec && sec.name) || `Section ${i + 1}`}
              spec={p}
              defaultOpen={i === 0}
            />
          );
        })}
      </div>
    </div>
  );
}
